import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMarketingSettings, useUpdateMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import { useInviteRotation, useUpdateInviteRotation } from "@/hooks/sms/useInviteRotation";
import { formatHourLabel } from "@/utils/inviteForecast";
import type { InviteRotation } from "@/types/sms";

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const FOLLOW_UP_OPTIONS = [0, 1, 2] as const;

/**
 * Timing controls for Order Invite (send time, follow-up cooldown, follow-ups,
 * monthly cap), writing the one sms_invite_rotations row plus
 * sms_marketing_settings.invite_followup_cooldown_days. The order window is
 * not a setting: every batch covers the previous store-local calendar day
 * (_shared/inviteWindow.ts). Bounded number inputs clamp; changes apply from
 * the next batch (today's queued invites still go out).
 */
export function InviteTimingCard() {
  const { data: settings } = useMarketingSettings();
  const updateSettings = useUpdateMarketingSettings();
  const { data: rotation } = useInviteRotation();
  const updateRotation = useUpdateInviteRotation();

  const [form, setForm] = useState<Partial<InviteRotation>>({});
  const [cooldownDays, setCooldownDays] = useState(30);

  useEffect(() => {
    if (rotation) setForm(rotation);
  }, [rotation]);
  useEffect(() => {
    if (settings) {
      setCooldownDays(settings.invite_followup_cooldown_days ?? 30);
    }
  }, [settings]);

  if (!settings || !rotation) return null;

  const sendHour = parseInt((form.send_time ?? rotation.send_time ?? "11:00:00").slice(0, 2), 10) || 0;
  const sendLabel = formatHourLabel(sendHour);
  const followUps = form.follow_ups ?? rotation.follow_ups ?? 1;

  const windowSentence = `At ${sendLabel} each day, everyone who ordered yesterday is invited.`;

  const cooldownSentence =
    followUps === 0
      ? "No follow-up texts are sent."
      : followUps === 1
        ? "They get one follow-up, and only if they order again after that."
        : "They get up to two follow-ups, and only if they order again after that.";

  const saving = updateRotation.isPending || updateSettings.isPending;

  const save = () => {
    updateRotation.mutate({
      store_id: rotation.store_id,
      send_time: `${String(sendHour).padStart(2, "0")}:00:00`,
      follow_ups: followUps,
      monthly_cap: form.monthly_cap ?? null,
    });
    updateSettings.mutate({
      store_id: settings.store_id,
      invite_followup_cooldown_days: cooldownDays,
    });
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <h3 className="text-base font-semibold text-foreground">Timing</h3>

        <div className="space-y-1.5">
          <Label htmlFor="invite-send-time">Daily send time</Label>
          <Select
            value={String(sendHour)}
            onValueChange={(v) => setForm((prev) => ({ ...prev, send_time: `${v.padStart(2, "0")}:00:00` }))}
          >
            <SelectTrigger id="invite-send-time" className="h-11">
              <SelectValue>{sendLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => (
                <SelectItem key={h} value={String(h)}>{formatHourLabel(h)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs leading-relaxed text-muted-foreground">{windowSentence}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="invite-cooldown-days">Follow-up cooldown (days)</Label>
          <Input
            id="invite-cooldown-days"
            type="number"
            min={1}
            max={90}
            className="h-11"
            value={cooldownDays}
            onChange={(e) => setCooldownDays(parseInt(e.target.value, 10) || 30)}
          />
          <p className="text-xs leading-relaxed text-muted-foreground">{cooldownSentence}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="invite-follow-ups">Follow-ups</Label>
            <Select
              value={String(followUps)}
              onValueChange={(v) => setForm((prev) => ({ ...prev, follow_ups: parseInt(v, 10) as InviteRotation["follow_ups"] }))}
            >
              <SelectTrigger id="invite-follow-ups" className="h-11">
                <SelectValue>{followUps}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {FOLLOW_UP_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-monthly-cap">Monthly cap</Label>
            <Input
              id="invite-monthly-cap"
              type="number"
              min={1}
              placeholder="No cap"
              className="h-11"
              value={form.monthly_cap ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                const parsed = parseInt(v, 10);
                setForm((prev) => ({ ...prev, monthly_cap: v === "" || Number.isNaN(parsed) ? null : parsed }));
              }}
            />
          </div>
        </div>

        <div>
          <Button type="button" className="h-11 sm:h-10" disabled={saving} onClick={save}>
            Save timing
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Changes apply from the next daily batch. Invites already queued today still go out.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
